import { motion } from "framer-motion";
import {
  Copy, Scissors, Check, Loader2, LayoutGrid,
  AlertCircle, ExternalLink, Share2, Save, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { GenerationProgress } from "@/components/generation-progress";
import { SocialIcon } from "@/components/social-icon";
import { getCharCountClass } from "@/lib/post-utils";

type ErrorCategory = "rate-limit" | "idea-too-short" | "ai-failure" | "generic";

function classifyError(msg: string): ErrorCategory {
  if (/too many request|rate limit|wait a moment/i.test(msg)) return "rate-limit";
  if (/needs a bit more context|at least a few words|describe what happened/i.test(msg)) return "idea-too-short";
  if (/ai returned|empty response|couldn't generate|couldn't process/i.test(msg)) return "ai-failure";
  return "generic";
}

const ERROR_META: Record<ErrorCategory, { title: string; hint: string; iconClass: string; panelClass: string }> = {
  "rate-limit": {
    title: "You're generating too fast",
    hint: "Wait about 60 seconds and try again — there's a limit of 10 generations per minute.",
    iconClass: "text-amber-400",
    panelClass: "bg-amber-500/5 border-amber-500/20",
  },
  "idea-too-short": {
    title: "Your idea needs more context",
    hint: "Describe what happened, what you learned, or the point you want to make — even a sentence or two is enough.",
    iconClass: "text-yellow-400",
    panelClass: "bg-yellow-500/5 border-yellow-500/20",
  },
  "ai-failure": {
    title: "The AI couldn't process that",
    hint: "Try rephrasing your idea or adding a bit more detail. Sometimes a different angle helps.",
    iconClass: "text-destructive",
    panelClass: "bg-destructive/5 border-destructive/20",
  },
  "generic": {
    title: "Generation failed",
    hint: "",
    iconClass: "text-destructive",
    panelClass: "bg-destructive/5 border-destructive/20",
  },
};

const PLATFORM_CONFIG = {
  twitter:   { label: "Twitter/X",  color: "bg-sky-500/10 border-sky-500/30 text-sky-400",     charLimit: 280   },
  linkedin:  { label: "LinkedIn",   color: "bg-blue-600/10 border-blue-600/30 text-blue-400",  charLimit: 3000  },
  reddit:    { label: "Reddit",     color: "bg-orange-500/10 border-orange-500/30 text-orange-400", charLimit: 40000 },
  instagram: { label: "Instagram",  color: "bg-pink-500/10 border-pink-500/30 text-pink-400",  charLimit: 2200  },
  facebook:  { label: "Facebook",   color: "bg-indigo-500/10 border-indigo-500/30 text-indigo-400", charLimit: 63206 },
};

export interface PostGeneratorAllPlatformsProps {
  generateAllError: string | null;
  isAllLoading: boolean;
  allPlatformResults: Record<string, string> | null;
  editedAllResults: Record<string, string>;
  setEditedAllResults: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  savedToVault: boolean;
  currentIdea: string;
  copied: string | null;
  trimmingPlatform: string | null;
  createIdeaMutationPending: boolean;
  handleGenerate: () => void;
  handleSaveToVault: () => void;
  handleCopy: (content: string, key?: string) => void;
  handleShare: (content: string, key?: string) => void;
  handleTrimAllPlatform: (p: string) => void;
}

export function PostGeneratorAllPlatforms({
  generateAllError,
  isAllLoading,
  allPlatformResults,
  editedAllResults,
  setEditedAllResults,
  savedToVault,
  currentIdea,
  copied,
  trimmingPlatform,
  createIdeaMutationPending,
  handleGenerate,
  handleSaveToVault,
  handleCopy,
  handleShare,
  handleTrimAllPlatform,
}: PostGeneratorAllPlatformsProps) {
  return (
    <div className="space-y-4">
      {generateAllError && !isAllLoading ? (
        (() => {
          const cat = classifyError(generateAllError);
          const meta = ERROR_META[cat];
          return (
            <div className={`bg-card rounded-3xl p-12 flex flex-col items-center justify-center gap-5 text-center border ${meta.panelClass}`}>
              <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center">
                <AlertCircle className={`w-7 h-7 ${meta.iconClass}`} />
              </div>
              <div className="space-y-2 max-w-xs">
                <p className="font-semibold text-foreground text-lg">{meta.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{generateAllError}</p>
                {meta.hint && <p className="text-xs text-muted-foreground/80 leading-relaxed italic">{meta.hint}</p>}
              </div>
              {cat !== "rate-limit" && (
                <Button
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={!currentIdea.trim()}
                  className="rounded-xl gap-2"
                >
                  <RefreshCw className="w-4 h-4" /> Try Again
                </Button>
              )}
            </div>
          );
        })()
      ) : !allPlatformResults && !isAllLoading ? (
        <div className="bg-card border border-border rounded-3xl p-12 flex flex-col items-center justify-center text-muted-foreground opacity-50">
          <LayoutGrid className="w-16 h-16 mb-4" />
          <p className="font-medium text-lg">See your post adapted for all 5 platforms</p>
          <p className="text-sm mt-2">Each one is uniquely optimized for how that platform works</p>
        </div>
      ) : isAllLoading ? (
        <div className="bg-card border border-border rounded-3xl p-8">
          <GenerationProgress isActive={true} />
        </div>
      ) : allPlatformResults ? (
        <div className="space-y-4">
          {!savedToVault ? (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 flex items-center justify-between">
              <p className="text-sm text-primary font-medium flex items-center gap-2">
                <Save className="w-4 h-4" /> Save all posts to your Idea Vault
              </p>
              <Button
                size="sm"
                className="rounded-lg bg-primary hover:bg-primary/90"
                onClick={handleSaveToVault}
                disabled={createIdeaMutationPending}
              >
                {createIdeaMutationPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> Save to Vault</>
                )}
              </Button>
            </div>
          ) : (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              <p className="text-sm text-emerald-400 font-medium">Saved to your Idea Vault</p>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {(["twitter", "linkedin", "reddit", "instagram", "facebook"] as const).map((p) => {
              const cfg = PLATFORM_CONFIG[p];
              const edited = editedAllResults[p] ?? allPlatformResults[p] ?? "";
              const isCopied = copied === p;
              const pCharCount = edited.length;
              const isOverLimit = p === "twitter" && pCharCount > 280;
              return (
                <motion.div
                  key={p}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${cfg.color}`}>
                      <SocialIcon platform={p} size={20} /> {cfg.label}
                    </div>
                    <div className="flex items-center gap-2">
                      {isOverLimit && (
                        <button
                          data-testid={`trim-to-limit-${p}-btn`}
                          onClick={() => handleTrimAllPlatform(p)}
                          disabled={trimmingPlatform === p}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500/10 border border-red-500/25 text-red-400 hover:bg-red-500/20 text-[10px] font-semibold transition-colors disabled:opacity-50"
                        >
                          {trimmingPlatform === p ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />}
                          Trim to 280
                        </button>
                      )}
                      <span className={`text-xs font-mono ${getCharCountClass(pCharCount, cfg.charLimit)}`}>
                        {pCharCount.toLocaleString()}/{cfg.charLimit.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <textarea
                    data-testid={`post-output-textarea-${p}`}
                    className="text-sm leading-relaxed text-foreground bg-transparent resize-y outline-none border border-border/40 hover:border-border focus:border-primary/40 focus:ring-1 focus:ring-primary/20 rounded-xl p-2.5 min-h-[120px] max-h-48 transition-colors w-full"
                    value={edited}
                    onChange={(e) => setEditedAllResults((prev) => ({ ...prev, [p]: e.target.value }))}
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid={`copy-${p}-btn`}
                      onClick={() => handleCopy(edited, p)}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl bg-secondary hover:bg-secondary/80 text-sm font-medium transition-colors"
                    >
                      {isCopied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                      {isCopied ? "Copied!" : "Copy"}
                    </button>
                    {p === "twitter" && edited && (
                      <button
                        data-testid="open-in-twitter-all-btn"
                        onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(edited)}`, "_blank")}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 hover:bg-sky-500/20 text-sm font-medium transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    )}
                    {p === "linkedin" && edited && (
                      <button
                        data-testid="open-in-linkedin-all-btn"
                        onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&summary=${encodeURIComponent(edited)}`, "_blank")}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-blue-600/10 border border-blue-600/20 text-blue-400 hover:bg-blue-600/20 text-sm font-medium transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      data-testid={`share-${p}-btn`}
                      onClick={() => handleShare(edited, `share-${p}`)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-secondary hover:bg-secondary/80 text-sm font-medium transition-colors"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
